import router from '@/main/router.js'
import requestManager from '@/utils/requestManager'

// Module-level state (persists across HMR / store resets)
let moduleInitialized = false
let visibilityChangeListener = null
let restartTimeout = null
let userReviewsInterval = null // runs regardless of tab visibility

export default {
  namespaced: true,
  state: {
    intervals: [],
    isTabActive: true,
    isInitialized: false
  },
  mutations: {
    SET_TAB_ACTIVE (state, isActive) {
      state.isTabActive = isActive
    },
    SET_INITIALIZED (state, value) {
      state.isInitialized = value
    },
    ADD_INTERVAL (state, intervalId) {
      state.intervals.push(intervalId)
    },
    CLEAR_INTERVALS (state) {
      state.intervals.forEach(intervalId => clearInterval(intervalId))
      state.intervals = []
    }
  },
  actions: {
    startAutoRefresh: ({ dispatch, commit, state, rootGetters }) => {
      // Gate polling until user is fully authenticated and NOT in a pending 2FA flow
      const canPoll = () => rootGetters['auth/isLoggedIn'] &&
                !rootGetters['auth/pendingTwoFactorChallenge']

      // Optional: do not even initialize until we are allowed to poll
      if (!canPoll()) {
        return
      }

      // prevent multiple inits
      if (moduleInitialized || state.isInitialized) return
      moduleInitialized = true
      commit('SET_INITIALIZED', true)

      // helper to register guarded intervals (tab visibility + auth/2FA)
      const registerInterval = (callback, intervalMilliseconds) => {
        const intervalId = setInterval(() => {
          if (!state.isTabActive) return
          if (!canPoll()) return
          callback()
        }, intervalMilliseconds)
        commit('ADD_INTERVAL', intervalId)
      }

      // Function to set up all intervals and make initial calls
      const setupIntervals = (makeInitialCalls = false) => {
        // Clear any existing intervals first
        commit('CLEAR_INTERVALS')

        // CLICK TICKETS
        if (router.canAccessPage('click_tickets')) {
          if (makeInitialCalls && state.isTabActive && canPoll()) {
            dispatch('click_ticket/getNotReviewedCount', null, { root: true })
          }
          registerInterval(() => {
            dispatch('click_ticket/getNotReviewedCount', null, { root: true })
          }, 30000) // 30 seconds
        }

        // SYSTEM ALERTS
        if (router.canAccessPage('system_alerts')) {
          if (makeInitialCalls && state.isTabActive && canPoll()) {
            dispatch('system_alert/getNotSeenCount', null, { root: true })
          }
          registerInterval(() => {
            dispatch('system_alert/getNotSeenCount', null, { root: true })
          }, 60000)
        }

        // USER REVIEWS – special case: always refresh regardless of tab visibility,
        // but still respect auth/2FA (canPoll)
        if (router.canAccessPage('user_reviews')) {
          if (makeInitialCalls && state.isTabActive && canPoll()) {
            dispatch('user/countUserNeedReview', null, { root: true })
          }
          if (!userReviewsInterval) {
            userReviewsInterval = setInterval(() => {
              if (!canPoll()) return
              dispatch('user/countUserNeedReview', null, { root: true })
              // keep the table refresh toggle behavior
              commit('user/TOGGLE_REFRESH_REVIEWS_TABLE', null, { root: true })
            }, 30000)
          }
        }

        // SURVEY ATTACKS → APPS
        if (router.canAccessPage('survey_attacks_apps')) {
          if (makeInitialCalls && state.isTabActive && canPoll()) {
            dispatch('survey/getSurveyAttacksAppsNotReviewedCount', null, { root: true })
          }
          registerInterval(() => {
            dispatch('survey/getSurveyAttacksAppsNotReviewedCount', null, { root: true })
          }, 30000)
        }

        // CHAT DASHBOARD (toggle only)
        if (router.canAccessPage('chat_dashboard')) {
          registerInterval(() => {
            commit('chat/TOGGLE_REFRESH_DASHBOARD_TABLE', null, { root: true })
          }, 60000)
        }

        // Admin info / FCM subscription: Hourly + optional initial
        if (makeInitialCalls && canPoll()) {
          dispatch('admin-info/fetchAdminInfo', null, { root: true })
        }
        registerInterval(() => {
          dispatch('admin-info/fetchAdminInfo', null, { root: true })
        }, 3600000) // 1 hour

        registerInterval(() => {
          dispatch('session/checkSession', null, { root: true })
        }, 3600000) // 1 hour
      }

      // Tab visibility listener
      const handleVisibilityChange = () => {
        const wasActive = state.isTabActive
        const isActive = !document.hidden
        commit('SET_TAB_ACTIVE', isActive)

        if (!isActive) {
          // stop page-specific intervals (keep userReviewsInterval running)
          commit('CLEAR_INTERVALS')

          // cancel pending API requests
          requestManager.cancelAll()

          // Clear any pending restart timeout
          if (restartTimeout) {
            clearTimeout(restartTimeout)
            restartTimeout = null
          }
        } else if (!wasActive && isActive) {
          // restart page-specific intervals after small delay
          if (state.intervals.length === 0) {
            // Clear any existing restart timeout
            if (restartTimeout) {
              clearTimeout(restartTimeout)
            }

            // Wait 2 seconds before restarting (prevents requests on quick tab switches)
            restartTimeout = setTimeout(() => {
              if (state.isTabActive && !document.hidden && state.intervals.length === 0) {
                setupIntervals(true)
              }
              restartTimeout = null
            }, 2000)
          }
        }
      }

      // Ensure we do not stack multiple listeners
      if (visibilityChangeListener) {
        document.removeEventListener('visibilitychange', visibilityChangeListener)
      }
      visibilityChangeListener = handleVisibilityChange
      document.addEventListener('visibilitychange', visibilityChangeListener)

      // seed initial tab state
      commit('SET_TAB_ACTIVE', !document.hidden)

      // initial setup
      if (state.intervals.length === 0) {
        setupIntervals(true)
      }
    },

    stopAutoRefresh: ({ commit }) => {
      // clear page-specific intervals
      commit('CLEAR_INTERVALS')

      // clear user reviews interval
      if (userReviewsInterval) {
        clearInterval(userReviewsInterval)
        userReviewsInterval = null
      }

      // clear restart timer
      if (restartTimeout) {
        clearTimeout(restartTimeout)
        restartTimeout = null
      }

      // remove visibility listener
      if (visibilityChangeListener) {
        document.removeEventListener('visibilitychange', visibilityChangeListener)
        visibilityChangeListener = null
      }

      // reset init flags
      commit('SET_INITIALIZED', false)
      moduleInitialized = false
    }
  }
}
